<script setup lang="ts">
/**
 * The pre-auth landing page: one cinematic sortie told by scrolling, on the
 * game's own live sea, in the same painted kit as the game's UI. This file is
 * only the page: it owns the scroller (`#app` is `overflow: hidden`, so every
 * non-scene screen opens its own), the CMS data for notices and FAQ, the
 * overlays, and which section the top bar marks; every section is a component
 * in `sections/`.
 *
 * Nothing waits on a fetch before PLAY FREE is clickable: notices, FAQ and the
 * footer rows each tolerate a slow or absent server and draw nothing for what
 * they lack.
 *
 * Emits `play` (open the enlistment form) and `signIn`; `App.vue` listens.
 */
import { onBeforeUnmount, onMounted, provide, ref } from 'vue';

import { reducedMotion } from '@/engine/motion';
import { api, type FaqEntry, type NewsArticle, type NewsSummary } from '@/net/api';
import LegalOverlay from '@/ui/LegalOverlay.vue';

import { NAV } from './landing-content';
import { landingSound } from './landing-sound';
import { useParallax } from './parallax';
import { LANDING_SCROLLER } from './scroller';
import FaqSection from './sections/FaqSection.vue';
import FinalCall from './sections/FinalCall.vue';
import FleetSection from './sections/FleetSection.vue';
import HarbourSection from './sections/HarbourSection.vue';
import HeroSection from './sections/HeroSection.vue';
import LandingFooter from './sections/LandingFooter.vue';
import LandingTopBar from './sections/LandingTopBar.vue';
import ModesSection from './sections/ModesSection.vue';
import NoticeOverlay from './sections/NoticeOverlay.vue';
import NoticesSection from './sections/NoticesSection.vue';
import RanksSection from './sections/RanksSection.vue';
import { useSlam } from './slam';

const emit = defineEmits<{ play: []; signIn: [] }>();

const landingEl = ref<HTMLElement | null>(null);
provide(LANDING_SCROLLER, landingEl);
useParallax(() => landingEl.value);
useSlam(() => landingEl.value);

// ─── The top bar: where to scroll, and which tab is lit. ────────────────────
const active = ref('');
/** A section counts as current once its top is above this fraction of the viewport. */
const ACTIVE_LINE = 0.4;
let raf = 0;

function markActive(): void {
  raf = 0;
  const root = landingEl.value;
  if (!root) return;
  const line = root.clientHeight * ACTIVE_LINE;
  let now = '';
  for (const item of NAV) {
    const el = root.querySelector<HTMLElement>(`[data-section="${item.id}"]`);
    if (el && el.getBoundingClientRect().top <= line) now = item.id;
  }
  active.value = now;
}

function onScroll(): void {
  if (!raf) raf = requestAnimationFrame(markActive);
}

function goto(id: string): void {
  const el = landingEl.value?.querySelector<HTMLElement>(`[data-section="${id}"]`);
  el?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
}

// ─── CMS: notices (first four) and FAQ. ────────────────────────────────────
const notices = ref<NewsSummary[]>([]);
const faq = ref<FaqEntry[]>([]);
const openNotice = ref<NewsSummary | null>(null);
const noticeArticle = ref<NewsArticle | null>(null);
const noticeLoading = ref(false);
const legalSlug = ref<string | null>(null);

async function showNotice(n: NewsSummary): Promise<void> {
  openNotice.value = n;
  noticeArticle.value = null;
  noticeLoading.value = true;
  try {
    noticeArticle.value = await api.newsArticle(n.slug);
  } catch {
    // Left null: the overlay falls back to the notice's own summary.
  } finally {
    noticeLoading.value = false;
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') openNotice.value = null;
}

onMounted(() => {
  landingSound.armOnGesture();
  window.addEventListener('keydown', onKeydown);
  landingEl.value?.addEventListener('scroll', onScroll, { passive: true });
  api
    .news(1)
    .then((page) => (notices.value = [...page.featured, ...page.items].slice(0, 4)))
    .catch(() => undefined);
  api
    .faq()
    .then((entries) => (faq.value = entries))
    .catch(() => undefined);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  landingEl.value?.removeEventListener('scroll', onScroll);
  if (raf) cancelAnimationFrame(raf);
});
</script>

<template>
  <div ref="landingEl" class="landing" data-test="landing-screen">
    <LandingTopBar :active="active" @play="emit('play')" @goto="goto" @top="goto('top')" />
    <HeroSection @play="emit('play')" @sign-in="emit('signIn')" />
    <FleetSection />
    <ModesSection />
    <RanksSection />
    <HarbourSection />
    <NoticesSection :notices="notices" @open="showNotice" />
    <FaqSection :entries="faq" />
    <FinalCall @play="emit('play')" />
    <LandingFooter @legal="legalSlug = $event" />

    <LegalOverlay :slug="legalSlug" @close="legalSlug = null" />
    <NoticeOverlay :notice="openNotice" :article="noticeArticle" :loading="noticeLoading" @close="openNotice = null" />
  </div>
</template>

<style scoped>
.landing {
  position: absolute;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;
  /* Sections paint over this; it shows only before their pictures arrive, so it is a picture too. */
  background: #0a1119 url('/art/loading-battle.webp') center / cover;
  padding-bottom: env(safe-area-inset-bottom);
}

@media (prefers-reduced-motion: reduce) {
  .landing {
    scroll-behavior: auto;
  }
}
</style>
